import { onMounted, onUnmounted, ref, type ComputedRef, type Ref } from 'vue'

export const HOTKEY_STORAGE_KEY = 'hotkeys'

export const HOTKEY_ACTIONS = [
  'powerToggle',
  'eqToggle',
  'rgbToggle',
  'aiToggle',
  'balenceToggle',
  'Positioning',
] as const

export type HotkeyAction = (typeof HOTKEY_ACTIONS)[number]
export type HotkeyMap = Partial<Record<HotkeyAction, string[]>>

export const DEFAULT_HOTKEYS: Record<HotkeyAction, string[]> = {
  powerToggle: ['Alt', '1'],
  eqToggle: ['Alt', '2'],
  rgbToggle: ['Alt', '3'],
  aiToggle: ['Alt', '4'],
  balenceToggle: ['Alt', '5'],
  Positioning: ['Alt', '6'],
}

type MaybeRef<T> = Ref<T> | ComputedRef<T>

interface UseHotkeysOptions {
  enabled: MaybeRef<boolean>
  settingOpen: MaybeRef<boolean>
  onTrigger: (action: HotkeyAction) => void | Promise<void>
  isActionAvailable?: (action: HotkeyAction) => boolean
}

const MODIFIER_KEYS = ['Control', 'Shift', 'Alt', 'Meta']

const readStoredHotkeys = (): HotkeyMap => {
  try {
    const stored = localStorage.getItem(HOTKEY_STORAGE_KEY)
    if (!stored) return {}
    const parsed = JSON.parse(stored) as HotkeyMap
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

const persistHotkeys = (hotkeys: HotkeyMap) => {
  localStorage.setItem(HOTKEY_STORAGE_KEY, JSON.stringify(hotkeys))
}

export const isValidHotkey = (keys: string[] | undefined): keys is string[] => {
  return Array.isArray(keys) && keys.length >= 2
}

export const normalizeHotkey = (keys: string[]) => {
  return keys.slice().sort().join('+')
}

export const formatHotkey = (keys: string[] | undefined, notSetText = 'Not Set') => {
  return isValidHotkey(keys) ? keys.join(' + ') : notSetText
}

const normalizeKeyName = (key: string) => {
  if (key === ' ') return 'Space'
  if (key.length === 1) return key.toUpperCase()
  return key
}

export const getHotkeyFromEvent = (event: KeyboardEvent) => {
  const keys: string[] = []
  if (event.ctrlKey) keys.push('Ctrl')
  if (event.shiftKey) keys.push('Shift')
  if (event.altKey) keys.push('Alt')
  if (event.metaKey) keys.push('Meta')

  if (!MODIFIER_KEYS.includes(event.key)) {
    keys.push(normalizeKeyName(event.key))
  }

  return keys
}

export function createHotkeyState(): HotkeyMap {
  const hotkeys: HotkeyMap = {
    ...DEFAULT_HOTKEYS,
    ...readStoredHotkeys(),
  }

  for (const action of HOTKEY_ACTIONS) {
    if (!isValidHotkey(hotkeys[action])) {
      delete hotkeys[action]
    }
  }

  return hotkeys
}

export function useHotkeys(options: UseHotkeysOptions) {
  const hotkeys = ref<HotkeyMap>(createHotkeyState())

  const resetHotkeys = () => {
    hotkeys.value = { ...DEFAULT_HOTKEYS }
    persistHotkeys(hotkeys.value)
  }

  const setHotkey = (action: HotkeyAction, keys: string[]) => {
    const next = { ...hotkeys.value }
    if (!isValidHotkey(keys)) {
      delete next[action]
    } else {
      next[action] = keys
    }
    hotkeys.value = next
    persistHotkeys(next)
  }

  const findConflict = (currentAction: HotkeyAction, keys: string[]) => {
    const current = normalizeHotkey(keys)
    return HOTKEY_ACTIONS.find(action => {
      const actionKeys = hotkeys.value[action]
      return action !== currentAction && isValidHotkey(actionKeys) && normalizeHotkey(actionKeys) === current
    })
  }

  const handleKeydown = (event: KeyboardEvent) => {
    if (!options.enabled.value || options.settingOpen.value) return

    const keys = getHotkeyFromEvent(event)
    if (!isValidHotkey(keys)) return

    const current = normalizeHotkey(keys)
    const action = HOTKEY_ACTIONS.find(item => {
      const actionKeys = hotkeys.value[item]
      return isValidHotkey(actionKeys) && normalizeHotkey(actionKeys) === current
    })

    if (!action || options.isActionAvailable?.(action) === false) return

    event.preventDefault()
    void options.onTrigger(action)
  }

  onMounted(() => {
    window.addEventListener('keydown', handleKeydown)
  })

  onUnmounted(() => {
    window.removeEventListener('keydown', handleKeydown)
  })

  return {
    hotkeys,
    setHotkey,
    resetHotkeys,
    findConflict,
  }
}
